import { useEffect, useState } from "react";
import { loadFinancialYears, fyLabel, computeClientFY } from "@/lib/fy";

/**
 * Compact FY selector. Value is the fy_start_year (integer) — or null for
 * "All time" when allowAllTime is set.
 *
 * Props:
 *   value: number | null
 *   onChange: (fy: number | null) => void
 *   allowAllTime: boolean (default false)
 *   testid: string (default "fy-select")
 *   label: string   — shown as a bc-label above the select. Defaults to
 *                     'Financial Year'. Pass '' to hide.
 */
export default function FYSelect({
  value,
  onChange,
  allowAllTime = false,
  testid = "fy-select",
  label = "Financial Year",
}) {
  const [years, setYears] = useState([]); // [{fy_start_year, label}, ...]

  useEffect(() => {
    let cancelled = false;
    loadFinancialYears().then((data) => {
      if (!cancelled) setYears(data.years || []);
    });
    return () => { cancelled = true; };
  }, []);

  const options = years.length
    ? years
    : [{ fy_start_year: computeClientFY(), label: fyLabel(computeClientFY()) }];

  return (
    <label className="flex flex-col text-xs">
      {label && <span className="bc-label mb-1">{label}</span>}
      <select
        value={value === null || value === undefined ? "" : String(value)}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "" ? null : parseInt(v));
        }}
        data-testid={testid}
        className="border border-[#E5E5E5] rounded-sm px-2.5 py-1.5 text-sm bg-white min-w-[130px]"
      >
        {allowAllTime && <option value="">All time</option>}
        {options.map((o) => (
          <option key={o.fy_start_year} value={o.fy_start_year}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
